import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';

import { StatisticsUnit } from '../../../../core/src/shared/statistics-config';
import { RouterOutput } from '../../api/trpc';

import { colors } from '../../theme/palette';
import { fontFamily } from '../../theme/typography';
import { FormatNumberOpts } from '../../utils/format-number';
import { ChartDataVariableId } from '../../utils/statistics';

import { CustomTooltip } from './chart-box.custom-tooltip';
import { Mode, Variant } from './chart-box.use-mode-select';
import { getSubStatColor } from './chart-box.utils';

type Options = {
  data: RouterOutput['getStats'];
  mode: Mode;
  unit: StatisticsUnit;
  yAxisDomain?: AxisDomain;
  yAxisTickFormatter: (x: any) => string;
  mobile: boolean;
  formatNumber: (value: number, opts: FormatNumberOpts) => string;
  maxValue?: number;
  variant: Variant.Line | Variant.LineStacked;
  variableId: ChartDataVariableId;
  getSubStatsLabel?: (key: string) => string;
};

export const getAreaChart = ({
  data,
  mode,
  unit,
  yAxisDomain,
  yAxisTickFormatter,
  mobile,
  formatNumber,
  variant,
  variableId,
  getSubStatsLabel,
}: Options) => {
  const stacked = variant === Variant.LineStacked && data.subStatsKeys.length;

  return (
    <AreaChart data={data.sortedData}>
      <CartesianGrid
        strokeWidth={0.25}
        stroke={colors.darkBlue}
        strokeOpacity={0.4}
        vertical={false}
      />
      <XAxis
        dataKey="at"
        style={{
          fontFamily,
          color: colors.darkBlue,
          top: 20,
          transform: 'translateY(8px)',
        }}
        type="number"
        scale="time"
        domain={['dataMin', 'dataMax']}
        axisLine={false}
        tickLine={false}
        interval={getTicksInterval(mode, data.sortedData)}
        minTickGap={30}
        tickFormatter={yAxisTickFormatter}
      />
      <YAxis
        width={getYAxisWidth(
          mobile,
          data.yAxisDomainMax ||
            Math.max(...data.sortedData.map((d) => d.value))
        )}
        style={{
          fontFamily,
          color: colors.darkBlue,
          transform: 'translateX(-6px)',
        }}
        axisLine={false}
        tickLine={false}
        tickFormatter={(value) => {
          const fractionDigits = unit === StatisticsUnit.Kilowatt ? 1 : 0;

          return formatNumber(value, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          });
        }}
        domain={yAxisDomain}
      />
      <Tooltip
        content={
          // eslint-disable-next-line
          // @ts-ignore
          <CustomTooltip
            mode={mode}
            unit={unit}
            variant={variant}
            variableId={variableId}
            getSubStatsLabel={getSubStatsLabel}
          />
        }
      />

      {!stacked && (
        <Area
          dataKey="value"
          name="kW"
          fill={`${colors.primary}33`}
          stroke={colors.primary}
        />
      )}

      {stacked &&
        data.subStatsKeys.map((key, index) => {
          const color = getSubStatColor(index, data.subStatsKeys.length);

          return (
            <Area
              dataKey={`subStats.${key}`}
              name="kW"
              stackId="1"
              fill={`${color}`}
              stroke={color}
              key={key}
            />
          );
        })}
    </AreaChart>
  );
};

const getYAxisWidth = (mobile: boolean, maxValue: number) => {
  if (mobile) {
    return maxValue > 8 ? 35 : 25;
  } else {
    return maxValue > 10 ? 45 : 35;
  }
};

const getTicksInterval = (mode: Mode, sortedData: unknown[]) => {
  if (mode === Mode.Day) {
    return undefined;
  } else if (mode === Mode.Week) {
    return Math.round(sortedData.length / 7);
  } else if (mode === Mode.Year) {
    return undefined;
  }

  return undefined;
};
